<template>
  <TitleCtaContainer :cta :subtitle :title>
    <div
      class="bg-surface-default-hi rounded-lg flex flex-col md:flex-row"
      :class="showThumbnails ? 'md:min-h-[580px]' : 'md:min-h-[440px]'"
    >
      <div v-if="image" class="md:shrink-0 md:grow-0 md:basis-380">
        <ImageRenderer
          v-bind="image"
          image-class="rounded-t-lg m-auto !h-200 w-full object-cover md:!h-full md:rounded-tr-none md:rounded-bl-lg"
        />
      </div>

      <div
        class="flex grow flex-col justify-between gap-16 overflow-hidden p-16 md:gap-24 md:p-24"
      >
        <div
          v-if="showThumbnails"
          ref="thumbnails-scroller"
          class="flex grow flex-col justify-between overflow-scroll scrollbar-none"
          :class="maskClass"
        >
          <div
            :aria-label="title || ''"
            class="mx-auto flex w-max items-start gap-12 overflow-x-auto overflow-y-hidden scrollbar-none"
            role="tablist"
          >
            <RevButtonBase
              v-for="(item, index) in carousels"
              :id="`tab-${index}`"
              :key="item.id"
              ref="tabItem"
              aria-controls="enhanced-carousel-panel"
              :aria-selected="currentCarouselIndex === index"
              class="flex w-72 hyphens-auto cursor-pointer flex-col items-center justify-center gap-4 text-center"
              data-test="recommendation-thumbnail"
              role="tab"
              :tabindex="currentCarouselIndex === index ? 0 : -1"
              @click="() => onThumbnailClick(index)"
              @keydown.left.prevent="() => onThumbnailClick(index - 1)"
              @keydown.right.prevent="() => onThumbnailClick(index + 1)"
            >
              <ImageRenderer
                alt=""
                class="bg-surface-default-low rounded-lg h-48 w-72 overflow-hidden border hover:border-static-default-hi"
                :class="
                  currentCarouselIndex === index
                    ? 'border-static-default-hi'
                    : 'border-transparent'
                "
                v-bind="omit(item.thumbnail, 'alt')"
                image-class="object-cover block"
              />
              <span class="text-static-default-hi caption block">
                {{ item.thumbnailName }}
              </span>
            </RevButtonBase>
          </div>
        </div>

        <div
          id="enhanced-carousel-panel"
          :aria-labelledby="`tab-${currentCarouselIndex}`"
          role="tabpanel"
        >
          <KeepAlive>
            <Suspense :key="cacheKey">
              <component
                :is="currentCarouselComponent"
                :key="cacheKey"
                v-bind="currentCarouselProps"
                :block-position
                class="!mb-0 max-w-full"
                :tracking="carouselTrackingData"
                @error="() => onCarouselErrorFn()"
                @reset-error="() => resetCurrentCarouselError()"
              />

              <template #fallback>
                <ProductCardCarouselSkeleton
                  aria-busy="true"
                  aria-live="polite"
                  :with-title="false"
                />
              </template>
            </Suspense>
          </KeepAlive>

          <EnhancedCarouselError
            v-if="showError"
            aria-live="polite"
            role="alert"
            @retry-clicked="retry"
          />
        </div>
      </div>
    </div>
  </TitleCtaContainer>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue'

import type { EnhancedCarouselApiProps } from '@backmarket/http-api/src/api-specs-content/models/enhanced-product-carousel'
import {
  type RefinedProductsApiProps,
  RefinedProductsLayout,
} from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import ProductCardCarouselSkeleton from '@backmarket/nuxt-layer-recommendation/ProductCardCarouselSkeleton.vue'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { omit } from '@backmarket/utils/object/omit'
import { RevButtonBase } from '@ds/components/ButtonBase'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import ImageRenderer from '../../shared-components/ImageRenderer/ImageRenderer.vue'
import TitleCtaContainer from '../../shared-components/TitleCtaContainer/TitleCtaContainer.vue'
import RefinedProducts, {
  type RefinedProductsBlockProps,
} from '../RefinedProducts/RefinedProducts.vue'

import EnhancedCarouselError from './EnhancedCarouselError.vue'
import { useCarouselComponentCache, useCarouselError } from './useCarouselError'

export type EnhancedCarouselBlockProps = EnhancedCarouselApiProps & BlockProps
const props = withDefaults(defineProps<EnhancedCarouselBlockProps>(), {
  tracking: () => ({}),
})

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error', 'reset-error', 'loaded'])

const tabItem = useTemplateRef('tabItem')
const productCategoriesScrollerRef = useTemplateRef<HTMLDivElement>(
  'thumbnails-scroller',
)
const { maskClass } = useScrollMaskClass(productCategoriesScrollerRef)

const showThumbnails = computed(() => props.carousels.length >= 2)
const { trackClick } = useTracking()

const logger = useLogger()

const currentCarouselIndex = ref(0)

const setCurrentCarousel = (index: number) => {
  currentCarouselIndex.value = index
}

const currentCarousel = computed(
  () => props.carousels[currentCarouselIndex.value]?.recommendation,
)

const { onCarouselError, showError, resetCurrentCarouselError } =
  useCarouselError()

const { registerErroredCarousel, getCacheKey, incrementRetries, retries } =
  useCarouselComponentCache()

const cacheKey = computed(
  () =>
    getCacheKey({
      index: currentCarouselIndex.value,
      id: currentCarousel.value?.id,
    }) + retries.value,
)

function retry() {
  resetCurrentCarouselError()
  incrementRetries()
}

function onCarouselErrorFn() {
  registerErroredCarousel(currentCarouselIndex.value)
  onCarouselError()
}

const carouselTrackingData = computed(() => {
  return {
    ...props.tracking,
    productRecoComponentPosition: currentCarouselIndex.value + 1,
    productComponent: `EnhancedCarousel_${currentCarousel.value?.type}`,
  }
})

function onThumbnailClick(i: number) {
  let index = i
  if (index < 0) index = props.carousels.length - 1
  if (index >= props.carousels.length) index = 0

  tabItem.value?.[index]?.$el.focus()

  resetCurrentCarouselError()
  setCurrentCarousel(index)
  trackClick({
    ...props.tracking,
    title: props.title || '',
    position: index + 1,
    name: props.carousels[index].thumbnailName || '',
  })
}

const currentCarouselComponent = computed(() => {
  // eslint-disable-next-line prefer-destructuring
  const type = currentCarousel.value?.type

  if (type === 'RefinedProducts') return RefinedProducts

  logger.error(
    `[CMS] [EnhancedProductCarousel] Unknown carousel type: ${type}`,
    { owners: ['bot-squad-content-front'] },
  )

  return null
})

function omitIrrelevantProps(carouselProps: RefinedProductsApiProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { title, subtitle, ctaLink, ...rest } = carouselProps || {}

  if ('productLayout' in rest) rest.productLayout = RefinedProductsLayout.SLIDER

  return rest
}

const currentCarouselProps = computed<RefinedProductsBlockProps>(() => {
  return omitIrrelevantProps(currentCarousel.value?.props)
})
</script>
